// Core
import { Fragment, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

// Chakra
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';

// Components
import { GiveawaysActiveIcon } from 'components/Icons/Icons';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Card from 'components/Card/Card';
import Field from 'components/Fields';
import ErrorCard from 'components/Errors';
import SuccessCard from '../../../../components/Success';
import { Controller } from 'react-hook-form';
import Attention from '../../../../components/Modals/Attention';
import PriceInput from '../../PriceInput';

import { PLAN_TYPES } from '../../../../constants/plan';
import usePermissionStatusByKey from '../../../../hooks/usePermissionStatusByKey';

const MarketPriceContent = ({
  form,
  onSubmit,
  isSubmitting,
  errorCard,
  isNew,
  handleDelete,
}) => {
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const history = useHistory();
  const handleCancelClick = useCallback(() => {
    history.push('/market-prices');
  }, [history]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = form;

  const codeOptions = [
    { value: '1', label: 'Unleaded E10' },
    { value: '2', label: 'Unleaded 91' },
    { value: '3', label: 'Premium 95' },
    { value: '4', label: 'Premium 98' },
    { value: '5', label: 'Diesel' },
    { value: '6', label: 'Premium Diesel' },
    { value: '7', label: 'Ad Blue' },
    { value: '8', label: 'LPG' },
  ];

  const isCreateMarketPrice = usePermissionStatusByKey(
    'POST:/admin/market-prices',
  );
  const isUpdateMarketPrice = usePermissionStatusByKey(
    'PATCH:/admin/market-prices/:id',
  );
  const isDeleteMarketPrice = usePermissionStatusByKey(
    'DELETE:/admin/market-prices/:id',
  );

  return (
    <Box zIndex="9" position="relative">
      {!isNew ? (
        <Breadcrumbs
          icon={GiveawaysActiveIcon}
          target="Market Prices"
          url="#/market-prices"
          current="Market Price"
          variant="standart"
        />
      ) : (
        <Breadcrumbs
          icon={GiveawaysActiveIcon}
          target="Market Prices"
          url="#/market-prices"
          current="New Market Price"
          variant="standart"
        />
      )}

      <form onSubmit={onSubmit}>
        <Flex
          gap="16px"
          position="absolute"
          mt="-92px"
          right="0"
          alignItems="center"
        >
          {isNew && (
            <Button
              variant="ghost-md"
              minW="120px"
              onClick={() => handleCancelClick()}
            >
              Cancel
            </Button>
          )}

          {!isNew && isDeleteMarketPrice && (
            <Button variant="ghost-md" w="120px" onClick={onOpenDelete}>
              Delete
            </Button>
          )}
          {(!isNew ? isUpdateMarketPrice : isCreateMarketPrice) && (
            <Button
              isLoading={isSubmitting}
              variant="submit-md"
              w="120px"
              type="submit"
            >
              {!isNew ? 'Confirm' : 'Create'}
            </Button>
          )}
        </Flex>

        {errorCard && <ErrorCard error={errorCard} maxW="840px" />}
        {message && <SuccessCard message={message} maxW={'100%'} />}
        {error && <ErrorCard error={error} maxW={'100%'} />}

        <Box maxW="840px">
          <Text variant="title-lg" mt="40px" mb="14px">
            Market Price info
          </Text>

          <Card padding="24px" mb="40px">
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <FormControl variant="floating" id="code" mb="24px">
                  <Select
                    value={field.value || ''}
                    placeholder="Select fuel type"
                    className={!errors.code ? 'input-valid' : 'input-invalid'}
                    onChange={(e) => {
                      const selectedValue = e.target.value;

                      field.onChange(selectedValue);

                      const selectedOption = codeOptions.find(
                        (opt) => opt.value === selectedValue,
                      );

                      const currentName = getValues('name');

                      if (!currentName && selectedOption) {
                        setValue('name', selectedOption.label, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }}
                  >
                    {codeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {`${option.value} - ${option.label}`}
                      </option>
                    ))}
                  </Select>

                  <FormLabel
                    htmlFor="code"
                    className={!errors.code ? 'input-valid' : 'input-invalid'}
                  >
                    Fuel Type
                  </FormLabel>

                  {errors.code && (
                    <FormHelperText className="input-error">
                      Select a fuel type
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Field
                  id="name"
                  label="Name"
                  type="text"
                  value={field.value}
                  isValid={!errors.name}
                  onChange={field.onChange}
                  mb="24px"
                />
              )}
            />

            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <Field
                  id="price"
                  label="Price"
                  type="number"
                  value={field.value}
                  isValid={!errors.price}
                  onChange={field.onChange}
                  mb="24px"
                />
              )}
            />

            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Table mb={6}>
                  <Tbody>
                    <Tr>
                      <Td verticalAlign="top" border="unset" p="0 0 0 0">
                        <Text variant="title">Is Active? </Text>
                      </Td>
                      <Td verticalAlign="top" border="unset" p="0 0 0 0">
                        <Switch
                          float="right"
                          isChecked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              )}
            />
          </Card>
        </Box>
      </form>
      <Attention
        variant="remove"
        handle={handleDelete}
        isOpen={isOpenDelete}
        onOpen={onOpenDelete}
        onClose={onCloseDelete}
      />
    </Box>
  );
};

export default MarketPriceContent;
